<template>
    <EChart
        card
        class="mt-3"
        :options="chartOptions"
        :header="$t('sales')"
        :error="request.getError()"
        :loading="loading"
        type="time"
    >
        <template #nav>
            <div :key="spaceId" class="d-flex gap-3">
                <FormCategoryTreeSelect
                    ref="FormCategoryTreeSelect"
                    :value="categories"
                    :disabled="request.getLoading()"
                    class="width-200"
                    preselect
                    prevent-empty-selection
                    :clearable="false"
                    @change="categories = $event"
                    @loading="categoriesLoading = $event"
                />
            </div>
        </template>
    </EChart>
</template>



<script>
    import { generateEChartData } from '@/utils/chart';
    import EChart from '@/components/common/EChart.vue';
    import FormCategoryTreeSelect from '@/components/common/FormCategoryTreeSelect.vue';

    export default {
        name: 'SalesChart',
        components: {
            EChart,
            FormCategoryTreeSelect,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                request: this.$req('space/SalesChart'),
                categories: [],
                categoriesLoading: false,
            };
        },
        computed: {
            loadingFilters() {
                return this.categoriesLoading;
            },
            loading() {
                return this.request.getLoading() || this.loadingFilters;
            },
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            timeRange() {
                return this.parentComponent.timeRange;
            },
            apiParams() {
                const urlParams = { spaceId: this.spaceId };
                const { startDate, endDate } = this.timeRange;
                const requestParams = { startDate, endDate };
                if (this.categories?.length) requestParams.categories = this.categories.join(',');
                return { urlParams, requestParams };
            },
            chartData() {
                return this.request.getData() || [];
            },
            chartOptions() {
                if (!this.chartData.length) {
                    return { series: [] };
                }
                const chartData = this.chartData.reduce(
                    (acc, item) => {
                        acc.amount.push([item.date, item.amount]);
                        acc.quantity.push([item.date, item.quantity]);
                        return acc;
                    },
                    { amount: [], quantity: [] }
                );

                return {
                    yAxis: [
                        {
                            type: 'value',
                            name: this.$t('dashboard.quantity'),
                        },
                        {
                            type: 'value',
                            name: this.$t('dashboard.salesAmount'),
                            position: 'right',
                        },
                    ],
                    series: [
                        {
                            name: this.$t('dashboard.quantity'),
                            type: 'line',
                            data: generateEChartData(this.timeRange, chartData.quantity),
                            areaStyle: null,
                        },
                        {
                            name: this.$t('dashboard.salesAmount'),
                            type: 'bar',
                            yAxisIndex: 1,
                            data: generateEChartData(this.timeRange, chartData.amount),
                            tooltip: {
                                valueFormatter: value => this.$format.currency(value),
                            },
                        },
                    ],
                };
            },
        },
        watch: {
            spaceId() {
                this.resetFilters();
            },
            apiParams: {
                immediate: true,
                handler(newVal, oldVal) {
                    if (JSON.stringify(newVal || {}) !== JSON.stringify(oldVal || {})) {
                        this.loadData();
                    }
                },
            },
        },
        methods: {
            loadData() {
                if (!this.loadingFilters) {
                    this.resetData();
                    this.fetchData();
                }
            },
            resetData() {
                this.request.abortAll();
                this.request.resetAll();
            },
            fetchData() {
                this.request.get(this.apiParams);
            },
            resetFilters() {
                this.categories = [];
                this.categoriesLoading = false;
            },
        },
        created() {
            this.parentComponent.$on('resetFilters', this.resetFilters);
        },
        beforeDestroy() {
            this.parentComponent.$off('resetFilters', this.resetFilters);
        },
    };
</script>
